import { Box, Flex, Image, Text } from "rebass/styled-components";
import styled from "styled-components";
import Countdown from "react-countdown";
import { useEffect } from "react";

import { CountDownRenderer } from "../../Views/Play/countdownTimer";
import { JoinCampaignButton } from "../JoinCampaignButton";
import { analytics } from "MVVM/Analytics";
import { IClipMarketingContent } from "../../@types";

export const CampaignContentCallout = ({
  compact,
  marketingContent,
  slug,
  endsAt,
  primaryColor,
  secondaryColor,
}: {
  compact?: boolean;
  marketingContent: IClipMarketingContent;
  slug: string;
  endsAt: string;
  primaryColor: string;
  secondaryColor: string;
}) => {
  useEffect(() => {
    analytics.track("Competition Content Callout - Loaded", {});
  }, []);

  if (!marketingContent.backgroundImage || !marketingContent.brandLogo) {
    return null;
  }

  if (compact)
    return (
      <Box mx={6} mt={6} mb={2}>
        <Container
          backgroundImage={marketingContent.backgroundImage}
          style={{ padding: "16px" }}
        >
          <Flex flexDirection="column" style={{ gap: 8 }}>
            <Flex style={{ gap: "20px" }}>
              <Image
                flexShrink={0}
                flexGrow={0}
                src={marketingContent.brandLogo}
                style={{ zIndex: 2, height: "48px", width: "128px" }}
              />
              <StyledText fontSize={1} fontWeight={500}>
                {marketingContent.copyClip}
              </StyledText>
            </Flex>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              mt={4}
              style={{ gap: "20px" }}
            >
              {endsAt && (
                <Countdown
                  date={new Date(endsAt)}
                  renderer={(props) => (
                    <CountDownRenderer
                      days={props.days}
                      hours={props.hours}
                      minutes={props.minutes}
                      seconds={props.seconds}
                      color={primaryColor || "#000000"}
                      variant="text.labelSmall"
                      showEndsIn={true}
                      showIcon={false}
                    />
                  )}
                />
              )}
              {slug && (
                <JoinCampaignButton
                  color={primaryColor || "#000000"}
                  secondaryColor={secondaryColor || "#000000"}
                  slug={slug}
                  style={{
                    padding: "2px",
                    height: "40px",
                    borderRadius: "20px",
                    lineHeight: "1em",
                    width: "140px",
                    zIndex: 0,
                  }}
                />
              )}
            </Flex>
          </Flex>
        </Container>
      </Box>
    );

  return (
    <Container backgroundImage={marketingContent.backgroundImage}>
      <Row>
        <CampaignColumn>
          <CampaignImageAndTimer>
            <Image
              width="180"
              flexShrink={0}
              src={marketingContent.brandLogo}
              style={{ zIndex: 2 }}
            />
            {endsAt && (
              <Countdown
                date={new Date(endsAt)}
                renderer={(props) => (
                  <CountDownRenderer
                    days={props.days}
                    hours={props.hours}
                    minutes={props.minutes}
                    seconds={props.seconds}
                    color={primaryColor || "#000000"}
                    variant="text.labelSmall"
                    showEndsIn={true}
                    showIcon={false}
                  />
                )}
              />
            )}
          </CampaignImageAndTimer>
        </CampaignColumn>
        <StyledText variant="text.labelMedium">
          {marketingContent.copyClip || ""}
        </StyledText>
      </Row>
      {slug && (
        <JoinCampaignButton
          color={primaryColor || "#000000"}
          secondaryColor={secondaryColor || "#000000"}
          slug={slug}
        />
      )}
    </Container>
  );
};

const StyledText = styled(Text)`
  z-index: 1;
  max-width: 600px;

  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    display: none;
  }
`;

const Row = styled(Flex)`
  gap: 20px;
  align-items: center;
  justify-content: start;
  width: 100%;
  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    justify-content: start;
    width: unset;
  }
`;

const CampaignImageAndTimer = styled(Flex)`
  flex-direction: column;
  gap: 4px;

  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    justify-content: start;
    width: 100%;
  }
`;

const Container = styled(Flex)<{ backgroundImage: string }>`
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${(props) => props.backgroundImage});
    background-size: cover;
    background-position: center;

    border-radius: 9px;
  }
  width: 100%;
  padding: 20px;
  position: relative;
  justify-content: space-between;
  border-radius: 9px;
  align-items: center;
  gap: 10px;
  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    justify-content: space-between;
  }
  @media (max-width: 600px) {
    &::before {
      background-position: top left; /* Shift the background image */
    }
  }
`;

const CampaignColumn = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;
